import { useRef } from "react"
import { CONTACT_KIND, kindsFor, splitDestination, joinDestination } from "../lib/destinationKinds"
import Listbox from "./Listbox"

// The destination field every surface shares: a kind picker (Website, Email)
// beside a text field that never shows the scheme. Controlled by the *full*
// destination string, so callers keep validating and saving exactly what they
// did before — the picker only decides which prefix goes in front.
// See lib/destinationKinds.js to add a kind.
//
// `placeholder` overrides the example for the default (Website) kind only, so a
// page can show a link in its own words (#340) and Email still shows an address.
//
// `contactPages` adds the hosted Contact page kind (#332). It owns no
// destination string, so the parent holds whether it is picked (`hosted`, told
// through `onHostedChange`) and renders the page's fields itself; with it
// picked, this renders the picker alone. Leaving it keeps whatever was typed
// for the other kind.
export default function DestinationInput({
  value,
  onChange,
  id,
  disabled,
  autoFocus,
  placeholder,
  pickerClassName = "",
  inputClassName = "",
  inputProps = {},
  contactPages = false,
  hosted = false,
  onHostedChange,
}) {
  const split = splitDestination(value)
  const kind = hosted ? CONTACT_KIND : split.kind
  const kinds = kindsFor(contactPages || hosted)
  const inputRef = useRef(null)
  // What the creator last typed, and the value it produced. splitDestination
  // hides a website's https:// on a value that came from outside (a saved
  // code, the mailto: fix); stripping it from their own keystrokes would make
  // "https://" vanish as it's typed and jump the caret.
  const typed = useRef(null)
  const text = typed.current && typed.current.value === value && !kind.prefix ? typed.current.text : split.text

  function type(raw) {
    const next = joinDestination(kind, raw)
    typed.current = { value: next, text: raw }
    onChange(next)
  }

  function pick(next) {
    typed.current = null
    if (next.hosted) {
      onHostedChange?.(true)
      return
    }
    if (hosted) onHostedChange?.(false)
    onChange(joinDestination(next, text))
    // The text field mounts again when leaving the hosted kind; focus it then.
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  if (hosted) {
    return (
      <div className="flex gap-2 min-w-0">
        <KindPicker kind={kind} kinds={kinds} onPick={pick} disabled={disabled} className={pickerClassName} />
      </div>
    )
  }

  return (
    <div className="flex gap-2 min-w-0">
      <KindPicker kind={kind} kinds={kinds} onPick={pick} disabled={disabled} className={pickerClassName} />
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode={kind.inputMode}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder={placeholder && !kind.prefix ? placeholder : kind.placeholder}
        value={text}
        onChange={(e) => type(e.target.value)}
        disabled={disabled}
        autoFocus={autoFocus}
        {...inputProps}
        className={"flex-1 min-w-0 " + inputClassName}
      />
    </div>
  )
}

function KindPicker({ kind, kinds, onPick, disabled, className }) {
  return (
    <Listbox
      options={kinds}
      value={kind.id}
      onChange={onPick}
      disabled={disabled}
      label="What the code opens"
      className={className}
      buttonProps={{ "aria-label": `What the code opens: ${kind.label}` }}
    />
  )
}
